import { ArrowDown, ArrowUp, Copy, LayoutTemplate, Trash2 } from 'lucide-react';
import type { TrainingModuleSchema, TrainingPageType } from '@hcc/types';
import { PAGE_META, pageGroupsFor } from './pageMeta';
import { SLIDE_TEMPLATES, SLIDE_TEMPLATE_LABEL, type SlideTemplate } from './moduleOps';
import { PicturePagesButton } from './PicturePagesButton';
import { InsertMenu, type InsertGroup } from './InsertMenu';
import { SortableList, SortableRow, DragHandle } from './SortableList';

const TEMPLATE_DESC: Record<SlideTemplate, string> = {
  title: 'Welcome / intro slide',
  section: 'A section divider',
  statement: 'A single key message',
};

/**
 * Ordered page list: select to edit, reorder with up/down (the codebase's
 * reorder idiom), delete, and the click-to-add palette underneath.
 */
export function PageListSidebar({
  schema,
  active,
  onSelect,
  onMove,
  onReorder,
  onRemove,
  onAdd,
  onAddTemplate,
  onAddPictures,
  onDuplicate,
  graded = true,
}: {
  schema: TrainingModuleSchema;
  active: number;
  /** False offers the unmarked-only page types (Q&A, follow-up) in the add menu. */
  graded?: boolean;
  onSelect: (index: number) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onReorder: (from: number, to: number) => void;
  onRemove: (index: number) => void;
  onAdd: (type: TrainingPageType) => void;
  onAddTemplate: (template: SlideTemplate) => void;
  onAddPictures: (urls: string[]) => void;
  onDuplicate: (index: number) => void;
}) {
  let questionNo = 0;
  const addGroups: InsertGroup[] = [
    ...pageGroupsFor(graded).map((g) => ({
      title: g.title,
      items: g.types.map((t) => ({
        key: t,
        label: PAGE_META[t].label,
        description: PAGE_META[t].description,
        icon: PAGE_META[t].icon,
        onSelect: () => onAdd(t),
      })),
    })),
    {
      title: 'Quick starts',
      items: SLIDE_TEMPLATES.map((tpl) => ({
        key: `tpl-${tpl}`,
        label: SLIDE_TEMPLATE_LABEL[tpl],
        description: TEMPLATE_DESC[tpl],
        icon: LayoutTemplate,
        onSelect: () => onAddTemplate(tpl),
      })),
    },
  ];
  return (
    <div className="space-y-3">
      <SortableList
        ids={schema.pages.map((p) => p.id)}
        onReorder={onReorder}
        className="space-y-1.5"
      >
        {schema.pages.map((p, i) => {
          // A grid holds several questions, so it advances the numbering by
          // its row count and is labelled with the range it covers.
          const onPage =
            p.type === 'slide' || p.type === 'follow_up'
              ? 0
              : p.type === 'question_group' || p.type === 'text_group'
                ? p.rows.length
                : 1;
          const isQuestion = onPage > 0;
          const firstNo = questionNo + 1;
          questionNo += onPage;
          // Snapshot the number for this row. The label lives in a render-prop
          // below that runs after this map finishes, so reading the mutable
          // questionNo there would show every question the final total.
          const qNo = onPage > 1 ? `${firstNo}-${questionNo}` : `${firstNo}`;
          const Icon = PAGE_META[p.type].icon;
          const isPicturePage =
            p.type === 'slide' &&
            !p.title &&
            p.blocks?.length === 1 &&
            p.blocks[0]?.type === 'image';
          const title =
            p.type === 'slide'
              ? p.title || (isPicturePage ? 'Picture page' : 'Content slide')
              : p.type === 'question_group' || p.type === 'text_group' || p.type === 'follow_up'
                ? p.title || PAGE_META[p.type].label
                : p.prompt || PAGE_META[p.type].label;
          return (
            <SortableRow
              key={p.id}
              id={p.id}
              className={(dragging) =>
                `flex items-center gap-1.5 rounded-token-sm border px-2 py-1.5 text-sm ${
                  i === active ? 'border-accent bg-accent/10' : 'border-line/10 bg-surface'
                } ${dragging ? 'shadow-token' : ''}`
              }
            >
              {({ dragHandle }) => (
                <>
                  <DragHandle dragHandle={dragHandle} label={`Reorder: ${title}`} />
                  <button
                    type="button"
                    onClick={() => onSelect(i)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-fg-muted" />
                    <span className="truncate text-fg">
                      {isQuestion ? `Q${qNo}. ` : ''}
                      {title}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label="Move page up"
                    disabled={i === 0}
                    onClick={() => onMove(i, -1)}
                    className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move page down"
                    disabled={i === schema.pages.length - 1}
                    onClick={() => onMove(i, 1)}
                    className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Duplicate page"
                    onClick={() => onDuplicate(i)}
                    className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete page"
                    disabled={schema.pages.length <= 1}
                    onClick={() => onRemove(i)}
                    className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </SortableRow>
          );
        })}
      </SortableList>
      <div>
        <span className="text-xs font-medium uppercase tracking-wide text-fg-dim">Add a page</span>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <InsertMenu triggerLabel="Add a page" groups={addGroups} />
          <PicturePagesButton onAdd={onAddPictures} />
        </div>
      </div>
    </div>
  );
}
